import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { sapApi } from '../services/api';
import '../styles/TBDataViewer.css';

function TBDataViewer({ entity, dataPoint, startDate, endDate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  useEffect(() => {
    loadData();
  }, [entity, dataPoint, startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    // Validate that startDate and endDate are provided
    if (!startDate || !endDate) {
      setError('Start date and end date are required to load TB data. This version may be from old data without date information.');
      setLoading(false);
      return;
    }

    try {
      // New API signature: getTBData(entityName, startDate, endDate, dataPoint)
      const response = await sapApi.getTBData(entity.name, startDate, endDate, dataPoint);
      setData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!data || !data.data) return [];

    if (!searchTerm) return data.data;

    return data.data.filter(row => {
      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Check if values are numbers
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportToExcel = () => {
    if (!data || !data.data) return;

    const worksheet = XLSX.utils.json_to_sheet(sortedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Trial Balance');

    const fileName = `${entity.name}_${dataPoint}_${data.timestamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return value;
  };

  if (loading) {
    return (
      <div className="tb-viewer-overlay">
        <div className="tb-viewer-modal">
          <div className="loading">Loading data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tb-viewer-overlay">
        <div className="tb-viewer-modal">
          <div className="error-message">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button onClick={onClose} className="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="tb-viewer-overlay">
        <div className="tb-viewer-modal">
          <div className="no-data">
            <h3>No Data Available</h3>
            <p>No trial balance data found for this entity.</p>
            <button onClick={onClose} className="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const columns = Object.keys(data.data[0]);

  return (
    <div className="tb-viewer-overlay" onClick={onClose}>
      <div className="tb-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tb-viewer-header">
          <div className="header-info">
            <h2>Trial Balance Data - {entity.finalyzer_company_code}</h2>
            <p className="metadata">
              {startDate && endDate && (
                <>
                  <strong>Date Range:</strong> {startDate} to {endDate} |{' '}
                </>
              )}
              <strong>Period:</strong> {data.period} |
              <strong> Timestamp:</strong> {data.timestamp} |
              <strong> Records:</strong> {data.record_count}
            </p>
          </div>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>

        <div className="tb-viewer-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search in all columns..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                &times;
              </button>
            )}
          </div>

          <button onClick={exportToExcel} className="btn btn-export">
            📊 Export to Excel
          </button>
        </div>

        <div className="results-info">
          Showing {paginatedData.length} of {sortedData.length} records
          {searchTerm && ` (filtered from ${data.data.length} total)`}
        </div>

        <div className="tb-table-container">
          <table className="tb-table">
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column} onClick={() => handleSort(column)}>
                    <div className="th-content">
                      {column}
                      {sortConfig.key === column && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, idx) => (
                <tr key={idx}>
                  {columns.map(column => (
                    <td key={column}>{formatValue(row[column])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-pagination"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-pagination"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TBDataViewer;
